/**
 * @fileoverview Inline editable field component supporting text, select, lookup, date, and tag inputs.
 * @module ui-admin/components/InlineEditableField
 *
 * Responsibilities:
 * - Render a field value that transitions to an edit input on click.
 * - Support multiple input types: text, textarea, number, select, lookup, date, tags.
 * - Integrate with lookup cache for lookup-type fields.
 *
 * Dependencies: useLookupCache, LookupValueModal, lucide-react icons.
 * Architecture: Presentation component — receives data and callbacks via props.
 * Notes: Lookup options display name only so Streamline source ids never appear as labels.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Pencil, Check, X, Loader2, Plus } from 'lucide-react';
import { useLookupCache } from '../../hooks/useLookupCache';
import { LookupValueModal } from '../lookups/LookupValueModal';
import type { LookupValue } from '../../services/lookupService';
import type { InlineEditableFieldProps } from './types';
import { formatPhone, validatePhone } from '../../utils/phoneFormatting';

const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

/**
 * @component InlineEditableField
 * Purpose: Double-click-to-edit field used by tenant-web occupancy/hydrant slide-outs.
 * Props: label, value, editValue, config, onSave, disabled, lookupAdminConfig.
 * Behavior: Lookup options display `name` only; draft value remains `code`.
 * Data Source: lookup values from useLookupCache or lookupAdminConfig.listLookupValues.
 * Constraints: presentation-only — persistence is the caller's onSave.
 * Performance: lookup list fetched once when entering edit mode.
 */
export default function InlineEditableField({
  label,
  value,
  span = 1,
  editValue,
  config,
  onSave,
  disabled = false,
  className = '',
  lookupAdminConfig,
}: InlineEditableFieldProps) {
  const { fieldKey, editType, options, lookupType, numberConfig, placeholder, validate } = config;

  const isReadonly = editType === 'readonly' || disabled;

  // ── State ──────────────────────────────────────────────────────
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<unknown>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [lookupValues, setLookupValues] = useState<LookupValue[]>([]);
  const [lookupFilter, setLookupFilter] = useState('');
  const [lookupOpen, setLookupOpen] = useState(false);
  const [lookupHighlight, setLookupHighlight] = useState(-1);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(null);
  const { getLookupValues, invalidate } = useLookupCache();

  // ── Derived ────────────────────────────────────────────────────
  const originalValue = editValue !== undefined ? editValue : (typeof value === 'string' ? value : '');

  const isDirty = useCallback(() => {
    if (editType === 'boolean') return draft !== originalValue;
    if (editType === 'number') return Number(draft) !== Number(originalValue);
    return String(draft ?? '') !== String(originalValue ?? '');
  }, [draft, originalValue, editType]);

  // ── Enter edit mode ────────────────────────────────────────────
  // Stop propagation so clicking the field to edit does not also trigger an
  // ancestor's click handler (e.g. a collapsible card/row that would otherwise
  // toggle shut and unmount this editor mid-edit).
  const enterEdit = useCallback(async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (isReadonly || editing) return;
    setDraft(originalValue);
    setError(null);
    setEditing(true);

    if (editType === 'lookup' && lookupType) {
      let initialFilter = '';
      try {
        const fetchFn = lookupAdminConfig?.listLookupValues
          ? () => lookupAdminConfig.listLookupValues!(lookupType)
          : () => getLookupValues(lookupType);
        const vals = await fetchFn();
        const activeVals = vals.filter((v) => v.isActive);
        setLookupValues(activeVals);

        const origStr = String(originalValue ?? '').trim();
        const matched = activeVals.find(
          (v) =>
            v.code === origStr ||
            v.name === origStr ||
            v.id === origStr ||
            String((v as any).legacySource?.sourceId) === origStr,
        );
        if (matched) {
          initialFilter = matched.name;
          setDraft(matched.code);
        } else if (typeof value === 'string' && value && value !== '—') {
          initialFilter = value;
        } else if (origStr && origStr !== '—') {
          initialFilter = origStr;
        }
      } catch {
        setLookupValues([]);
      }
      setLookupFilter(initialFilter);
      setLookupOpen(true);
      setLookupHighlight(-1);
    }
  }, [isReadonly, editing, originalValue, editType, lookupType, getLookupValues, lookupAdminConfig]);

  // ── Save ───────────────────────────────────────────────────────
  const doSave = useCallback(async () => {
    if (saving) return;
    // Built-in type validation before caller's validate
    if (editType === 'tel' && String(draft ?? '').length > 0 && !validatePhone(String(draft ?? ''))) {
      setError('Enter a 10-digit phone number');
      return;
    }
    if (editType === 'email' && String(draft ?? '').trim().length > 0 && !EMAIL_REGEX.test(String(draft ?? '').trim())) {
      setError('Enter a valid email address');
      return;
    }
    if (validate) {
      const err = validate(draft);
      if (err) { setError(err); return; }
    }
    setError(null);
    setSaving(true);
    try {
      await onSave(fieldKey, draft);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [saving, validate, draft, onSave, fieldKey, editType]);

  // ── Cancel ─────────────────────────────────────────────────────
  const doCancel = useCallback(() => {
    setEditing(false);
    setDraft(null);
    setError(null);
    setLookupOpen(false);
  }, []);

  // ── Click-away: dirty → save, clean → cancel ──────────────────
  useEffect(() => {
    if (!editing) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        if (isDirty()) {
          doSave();
        } else {
          doCancel();
        }
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [editing, isDirty, doSave, doCancel]);

  // ── Auto-focus input on edit ───────────────────────────────────
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLInputElement || inputRef.current instanceof HTMLTextAreaElement) {
        inputRef.current.select();
      }
    }
  }, [editing]);

  // ── Keyboard: Enter saves, Escape cancels ──────────────────────
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      doCancel();
    } else if (e.key === 'Enter') {
      if (editType === 'textarea' && !e.ctrlKey) return; // plain Enter in textarea = newline
      e.preventDefault();
      if (isDirty()) doSave(); else doCancel();
    }
  }, [editType, isDirty, doSave, doCancel]);

  // ── Lookup keyboard nav ────────────────────────────────────────
  const filteredLookups = lookupValues.filter((v) =>
    !lookupFilter || v.name.toLowerCase().includes(lookupFilter.toLowerCase()) || v.code.toLowerCase().includes(lookupFilter.toLowerCase())
  );

  const handleLookupKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setLookupHighlight((h) => Math.min(h + 1, filteredLookups.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setLookupHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && lookupHighlight >= 0 && filteredLookups[lookupHighlight]) {
      e.preventDefault();
      setDraft(filteredLookups[lookupHighlight].code);
      setLookupFilter(filteredLookups[lookupHighlight].name);
      setLookupOpen(false);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      doCancel();
    }
  }, [filteredLookups, lookupHighlight, doCancel]);

  // ── Render: readonly / display-only ────────────────────────────
  if (isReadonly) {
    return (
      <div className={`${span === 2 ? 'col-span-2' : ''} ${className}`}>
        {label && <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">{label}</div>}
        <div className="text-sm text-gray-800">{value || '—'}</div>
      </div>
    );
  }

  // ── Render: edit mode ──────────────────────────────────────────
  if (editing) {
    return (
      // Stop clicks inside the active editor from bubbling to a collapsible
      // ancestor (card/row) that would otherwise toggle shut while editing.
      <div ref={wrapperRef} onClick={(e) => e.stopPropagation()} className={`${span === 2 ? 'col-span-2' : ''} ${className}`}>
        {label && <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">{label}</div>}
        <div className="flex items-start gap-1">
          <div className="flex-1 min-w-0">
            {renderEditInput()}
            {error && <div className="text-[10px] text-red-500 mt-0.5">{error}</div>}
          </div>
          <div className="flex items-center gap-0.5 pt-1 shrink-0">
            <button
              onClick={doSave}
              disabled={saving}
              className="p-0.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-sm transition-colors disabled:opacity-50"
              title="Save"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            </button>
            <button
              onClick={doCancel}
              disabled={saving}
              className="p-0.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-sm transition-colors disabled:opacity-50"
              title="Cancel"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: display mode with visual indicators ────────────────
  return (
    <div className={`${span === 2 ? 'col-span-2' : ''} ${className}`}>
      {label && <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">{label}</div>}
      <div
        ref={wrapperRef}
        onClick={enterEdit}
        className="group/ief relative inline-flex items-center gap-1 w-full cursor-text"
        title="Click to edit"
      >
        <span className="text-sm text-gray-800 border-b border-dashed border-gray-300 group-hover/ief:border-solid group-hover/ief:border-orange-400 transition-colors pb-px">
          {renderDisplayValue()}
        </span>
        <Pencil className="h-3 w-3 text-gray-300 opacity-25 group-hover/ief:opacity-70 group-hover/ief:text-orange-500 transition-all shrink-0" />
      </div>
    </div>
  );

  // ── Display value renderer ─────────────────────────────────────
  function renderDisplayValue() {
    if (editType === 'boolean') {
      const boolVal = originalValue === true || originalValue === 'true';
      return (
        <span className="inline-flex items-center gap-1.5">
          <span className={`inline-block h-2 w-2 rounded-full ${boolVal ? 'bg-green-500' : 'bg-gray-300'}`} />
          {boolVal ? 'Yes' : 'No'}
        </span>
      );
    }
    if (value !== undefined && value !== null && value !== '') {
      if (typeof value === 'object') return value as React.ReactNode;
      return value;
    }
    return <span className="text-gray-400">—</span>;
  }

  // ── Edit input renderer ────────────────────────────────────────
  function renderEditInput() {
    const baseInputClass = 'w-full text-sm border border-gray-300 rounded-sm px-2 py-1 focus:outline-hidden focus:ring-1 focus:ring-orange-400 focus:border-orange-400 transition-colors';

    switch (editType) {
      case 'text':
        return (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="text"
            value={String(draft ?? '')}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={saving}
            className={baseInputClass}
            aria-label={label}
          />
        );

      case 'textarea':
        return (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={String(draft ?? '')}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={saving}
            rows={3}
            className={`${baseInputClass} resize-y min-h-[60px]`}
            aria-label={label}
          />
        );

      case 'number':
        return (
          <div className="flex items-center gap-1">
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type="number"
              value={draft !== null && draft !== undefined ? String(draft) : ''}
              onChange={(e) => setDraft(e.target.value === '' ? '' : Number(e.target.value))}
              onKeyDown={handleKeyDown}
              min={numberConfig?.min}
              max={numberConfig?.max}
              step={numberConfig?.step}
              placeholder={placeholder}
              disabled={saving}
              className={baseInputClass}
              aria-label={label}
            />
            {numberConfig?.suffix && <span className="text-xs text-gray-500 shrink-0">{numberConfig.suffix}</span>}
          </div>
        );

      case 'select':
        return (
          <select
            ref={inputRef as React.RefObject<HTMLSelectElement>}
            value={String(draft ?? '')}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={saving}
            className={baseInputClass}
            aria-label={label}
          >
            <option value="">— Select —</option>
            {(options ?? []).map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );

      case 'date':
        return (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="date"
            value={String(draft ?? '')}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={saving}
            className={baseInputClass}
            aria-label={label}
          />
        );

      case 'boolean':
        return (
          <button
            type="button"
            onClick={() => setDraft((prev: unknown) => !(prev === true || prev === 'true'))}
            disabled={saving}
            className="flex items-center gap-2 text-sm py-1"
            aria-label={label}
          >
            <div className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
              (draft === true || draft === 'true') ? 'bg-green-500' : 'bg-gray-300'
            }`}>
              <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transform transition-transform ${
                (draft === true || draft === 'true') ? 'translate-x-4' : 'translate-x-0.5'
              }`} />
            </div>
            <span className="text-gray-700">{(draft === true || draft === 'true') ? 'On' : 'Off'}</span>
          </button>
        );

      case 'tel': {
        const telValid = validatePhone(String(draft ?? ''));
        const telHasValue = String(draft ?? '').length > 0;
        return (
          <>
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type="tel"
              value={String(draft ?? '')}
              onChange={(e) => setDraft(formatPhone(e.target.value))}
              onKeyDown={handleKeyDown}
              placeholder={placeholder || '(555) 123-4567'}
              disabled={saving}
              className={`${baseInputClass} ${telHasValue && !telValid ? 'border-red-400 focus:ring-red-400 focus:border-red-400' : ''}`}
              aria-label={label}
            />
            {telHasValue && !telValid && (
              <div className="text-[10px] text-red-500 mt-0.5">Enter a 10-digit phone number</div>
            )}
          </>
        );
      }

      case 'email': {
        const emailHasValue = String(draft ?? '').trim().length > 0;
        const emailFormatValid = EMAIL_REGEX.test(String(draft ?? '').trim());
        return (
          <>
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type="email"
              value={String(draft ?? '')}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder || 'user@example.com'}
              disabled={saving}
              className={`${baseInputClass} ${emailHasValue && !emailFormatValid ? 'border-red-400 focus:ring-red-400 focus:border-red-400' : ''}`}
              aria-label={label}
            />
            {emailHasValue && !emailFormatValid && (
              <div className="text-[10px] text-red-500 mt-0.5">Enter a valid email address</div>
            )}
          </>
        );
      }

      case 'lookup':
        return (
          <div className="relative">
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type="text"
              value={lookupFilter}
              onChange={(e) => {
                setLookupFilter(e.target.value);
                setLookupOpen(true);
                setLookupHighlight(-1);
              }}
              onFocus={() => setLookupOpen(true)}
              onKeyDown={handleLookupKeyDown}
              placeholder={placeholder || 'Search...'}
              disabled={saving}
              className={baseInputClass}
              aria-label={label}
            />
            {lookupOpen && (filteredLookups.length > 0 || (lookupAdminConfig?.canCreate)) && (
              <div className="absolute z-50 top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
                {filteredLookups.map((lv, i) => {
                  const isSelected =
                    lv.code === draft ||
                    lv.name === draft ||
                    lv.id === draft ||
                    String((lv as any).legacySource?.sourceId) === String(draft);
                  return (
                    <button
                      key={lv.id}
                      type="button"
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-orange-50 transition-colors flex items-center justify-between ${
                        isSelected
                          ? 'bg-orange-50/70 font-semibold text-orange-800'
                          : i === lookupHighlight
                            ? 'bg-orange-50 text-orange-700'
                            : 'text-gray-700'
                      }`}
                      onMouseDown={(e) => {
                        e.preventDefault(); // prevent blur
                        setDraft(lv.code);
                        setLookupFilter(lv.name);
                        setLookupOpen(false);
                      }}
                    >
                      <div className="font-medium">{lv.name}</div>
                      {isSelected && <span className="text-orange-600 font-bold text-xs">✓</span>}
                    </button>
                  );
                })}
                {lookupAdminConfig?.canCreate && (
                  <button
                    type="button"
                    className="w-full text-left px-3 py-1.5 text-sm text-orange-600 hover:bg-orange-50 transition-colors border-t border-gray-100 flex items-center gap-1.5"
                    title={lookupAdminConfig.createDisabledReason}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setLookupOpen(false);
                      setShowCreateModal(true);
                    }}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    <span>Create new value</span>
                  </button>
                )}
              </div>
            )}
            {showCreateModal && lookupType && lookupAdminConfig && (
              <LookupValueModal
                value={null}
                lookupType={lookupType}
                scopeContext={lookupAdminConfig.scopeContext}
                existingCount={lookupValues.length}
                availableGroups={lookupAdminConfig.availableGroups ?? []}
                availableInspectionModules={lookupAdminConfig.availableInspectionModules ?? []}
                onClose={() => setShowCreateModal(false)}
                onSave={async (newVal) => {
                  const saved = await lookupAdminConfig.onSaveLookupValue(newVal);
                  invalidate(lookupType);
                  setLookupValues((prev) => [...prev, saved]);
                  setDraft(saved.code);
                  setLookupFilter(saved.name);
                  setShowCreateModal(false);
                  setLookupOpen(false);
                }}
              />
            )}
          </div>
        );

      default:
        return (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="text"
            value={String(draft ?? '')}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={saving}
            className={baseInputClass}
            aria-label={label}
          />
        );
    }
  }
}
